import React, { useState, useMemo, useEffect } from 'react';
import '../../../../styles/journey-demo.css';
import { JOURNEYS, JOURNEY_CARDS, JOURNEY_LABELS } from './journeyData';
import { getVisual } from './journeyVisuals';
import JourneyGate, { GATE_KEY, WATERMARK_KEY } from './JourneyGate';

const DILIGENTSIA = require('../../../../images/landing-pages/companies/journey-assets/diligentsia.png');
const CLARENCE = require('../../../../images/landing-pages/companies/journey-assets/clarence-transparent.png');

const KOALENDAR = 'https://koalendar.com/e/meet-with-mark-bernstein';
const INTRO = -1; // the "Which journey fits you?" cover; 0..n-1 are the walkthrough steps

/**
 * Builds the tiled, diagonal watermark as an SVG data URI, matching the VC sandbox's tile. The
 * colour is baked in, so the fill is the light-theme value — the demo panel is always light.
 *
 * @param {string} text - "email · ip · timestamp"
 * @returns {string} a data: URI for use as background-image
 */
const watermarkTile = (text) => {
    const tileW = 420, tileH = 210;
    const safe = String(text).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${tileW}" height="${tileH}">`
        + `<text x="${tileW / 2}" y="${tileH / 2}" transform="rotate(-24 ${tileW / 2} ${tileH / 2})" `
        + 'text-anchor="middle" dominant-baseline="middle" font-family="Poppins, Arial, sans-serif" '
        + `font-size="12" fill="rgba(60,64,90,0.22)">${safe}</text></svg>`;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svg)))}`;
};

/** sessionStorage is unavailable in some privacy modes, so every access is guarded. */
const readSession = (key) => {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
};
const writeSession = (key, value) => {
    try { sessionStorage.setItem(key, value); } catch (e) { /* storage unavailable */ }
};

/**
 * The 2-minute demo — "Which journey fits you?" plus all three walkthroughs.
 *
 * The handoff shipped one HTML file per journey, where picking a different card navigated to
 * another file. Here all three live in one component, so selecting a card just swaps the active
 * journey in place and resets to the cover.
 */
const JourneyDemo = () => {
    const [journey, setJourney] = useState(1);
    const [current, setCurrent] = useState(INTRO);
    // Which utility row is open beneath the tracker: 'time', 'cost', or neither.
    const [activeUtil, setActiveUtil] = useState(null);
    // The lead-capture gate stands between the cover and the first step. Passing it watermarks the
    // panel for the rest of the browser session, matching the VC sandbox.
    const [isGateOpen, setIsGateOpen] = useState(false);
    const [watermark, setWatermark] = useState('');

    useEffect(() => {
        if (readSession(GATE_KEY) === '1') setWatermark(readSession(WATERMARK_KEY) || '');
    }, []);

    const isUnlocked = !!watermark;

    const data = JOURNEYS[journey];
    const steps = data['steps'];
    const isIntro = current === INTRO;
    const step = isIntro ? null : steps[current];
    const lastIndex = steps.length - 1;

    // Re-selecting the same card is a no-op; switching journeys restarts at the cover.
    const chooseJourney = (n) => {
        if (n === journey) return;
        setJourney(n);
        setCurrent(INTRO);
    };

    // Leaving the cover is what starts the journey, so that is where the gate sits. Once passed it
    // stays passed for the session, including after a restart back to the cover.
    const onNext = () => {
        if (isIntro && !isUnlocked) return setIsGateOpen(true);
        setCurrent(current === lastIndex ? INTRO : current + 1);
    };

    const onGatePass = (wm) => {
        writeSession(GATE_KEY, '1');
        writeSession(WATERMARK_KEY, wm);
        setWatermark(wm);
        setIsGateOpen(false);
        setCurrent(0);
    };
    const onBack = () => { if (current > INTRO) setCurrent(current - 1); };
    const toggleUtil = (key) => setActiveUtil(activeUtil === key ? null : key);

    const visual = useMemo(
        () => (isIntro ? null : getVisual(journey, step['visual'])),
        [isIntro, journey, step],
    );

    return (
        <div className="dlg-journey-demo">
            {isGateOpen && <JourneyGate onPass={onGatePass} onClose={() => setIsGateOpen(false)} />}
            <div className="browser">
                {/* Inside .browser, not the section: the mark belongs to the sandbox card, and the
                    card's own overflow:hidden clips it to the rounded corners. */}
                {isUnlocked && (
                    <div className="jd-watermark" aria-hidden="true" style={{ backgroundImage: `url("${watermarkTile(watermark)}")` }} />
                )}
                <div className="chrome">
                    <div className="dots"><span /><span /><span /></div>
                    <div className="url">app.diligentsia.co.uk/how-it-works</div>
                    <div className="sample-tag"><span className="d" />Quick walkthrough</div>
                </div>

                <div className="topnav">
                    <div className="brand"><div className="mark"><img src={DILIGENTSIA} alt="Diligentsia" /></div>Diligentsia</div>
                    <div style={{ marginLeft: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px' }}>
                        {!isIntro && <span className="journey-badge" style={{ marginTop: 0 }}>{data['badge']}</span>}
                        <div className="subtitle" style={{ marginLeft: 0 }}>Clarence will explain each step as you go</div>
                    </div>
                </div>

                <div className="head">
                    <div className="eyebrow">{isIntro ? 'Getting Started' : 'Your journey'}</div>
                    <h1>{isIntro ? 'Which journey fits you?' : JOURNEY_LABELS[journey]}</h1>
                    <p>
                        {isIntro
                            ? 'We offer a number of bespoke company journeys — pick the one that matches where you are today.'
                            : `Step ${current + 1} of ${steps.length} — ${step['title'].split(' · ')[1]}`}
                    </p>
                </div>

                {!isIntro && (
                    <div className="util-row">
                        <button type="button" className={`util-btn${activeUtil === 'time' ? ' active' : ''}`} onClick={() => toggleUtil('time')}>Estimated Time</button>
                        <button type="button" className={`util-btn${activeUtil === 'cost' ? ' active' : ''}`} onClick={() => toggleUtil('cost')}>Cost</button>
                    </div>
                )}

                {!isIntro && (
                    <div className="tracker">
                        {steps.map((s, i) => (
                            <div
                                key={s['title']}
                                className={`tstep${i === current ? ' active' : ''}${i < current ? ' done' : ''}`}
                                onClick={() => (isUnlocked ? setCurrent(i) : setIsGateOpen(true))}
                            >
                                <div className="tline" />
                                <div className="tfree" style={i === 0 ? undefined : { visibility: 'hidden' }}>Free</div>
                                <div className="tcircle">{i + 1}</div>
                                <div className="tlabel">{s['title'].split(' · ')[1]}</div>
                            </div>
                        ))}
                    </div>
                )}

                {!isIntro && activeUtil === 'time' && (
                    <div className="time-row" style={{ display: 'flex' }}>
                        {data['timeEstimates'].map((t, i) => (
                            <div className={`time-chip${t['variable'] ? ' variable' : ''}${t['free'] ? ' free' : ''}`} key={i}>{t['label']}</div>
                        ))}
                    </div>
                )}
                {!isIntro && activeUtil === 'cost' && (
                    <>
                        <div className="cost-bar" style={{ display: 'block' }}>{data['costBar']}</div>
                        <div className="cost-note" style={{ display: 'block' }}>{data['costNote']}</div>
                    </>
                )}

                <div className="stage">
                    <div className="clarence-panel">
                        <img className="clarence-char" src={CLARENCE} alt="Clarence" />
                        <div className="right-col">
                            <div className={`step-visual${isIntro || visual ? ' show' : ''}${isIntro ? ' is-cover' : ''}`}>
                                {isIntro ? (
                                    <>
                                        <div className="vw-label">Choose your journey</div>
                                        <div className="journey-cards">
                                            {JOURNEY_CARDS.map(c => (
                                                <div
                                                    key={c['n']}
                                                    className={`journey-card${journey === c['n'] ? ' selected' : ''}`}
                                                    onClick={() => chooseJourney(c['n'])}
                                                >
                                                    <div className="jc-num">{c['n']}</div>
                                                    <div className="jc-title">{c['title']}</div>
                                                    <div className="jc-desc">{c['desc']}</div>
                                                    <label className="jc-radio-row">
                                                        <input
                                                            type="radio"
                                                            name="journeySelect"
                                                            value={c['n']}
                                                            checked={journey === c['n']}
                                                            onChange={() => chooseJourney(c['n'])}
                                                        />
                                                        Select this journey
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                ) : visual}
                            </div>

                            <div className="jd-bubble show">
                                <div className="jd-bubble-tag">{isIntro ? 'Before we begin' : `Step ${current + 1} of ${steps.length}`}</div>
                                <h2>{isIntro ? `Selected: ${JOURNEY_LABELS[journey]}` : step['title'].split(' · ')[1]}</h2>
                                <div>
                                    {isIntro ? (
                                        <>
                                            <p>Hi, I'm Clarence, our AI Manager. Let me explain your journey in 2 minutes.</p>
                                            {/* Reason: the intro copy is authored HTML from the handoff (it bolds the
                                                journey name), and it is a build-time constant — never user input. */}
                                            <p dangerouslySetInnerHTML={{ __html: data['introMessage'] }} />
                                        </>
                                    ) : <p>{step['text']}</p>}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="nav-row">
                        <button type="button" className="btn ghost" onClick={onBack} disabled={isIntro}>← Back</button>
                        <div className="progress-dots">
                            {steps.map((s, i) => (
                                <span key={s['title']} className={`${!isIntro && i === current ? 'active' : ''}${!isIntro && i < current ? ' done' : ''}`.trim()} />
                            ))}
                        </div>
                        <button type="button" className="btn primary" onClick={onNext}>
                            {current === lastIndex ? 'Restart from the top ↺' : 'Next →'}
                        </button>
                    </div>
                </div>

                <div className="footer-note">
                    This is a <b>live interactive sandbox</b> — nothing you click here affects real data.{' '}
                    <a href={KOALENDAR} target="_blank" rel="noopener noreferrer">Book a call</a> to see the full experience for your own company.
                </div>
            </div>
        </div>
    );
};

export default JourneyDemo;
